<script lang="ts" setup>
import type { SelectedItemType } from "../models/SelectedItem.type";
import type { SelectedItemsType } from "../models/SelectedItems.type";
import type { SelectionModeEnum } from "@lxc/app-device-common";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import type { User } from "~/modules/user/models/User.interface";

const props = defineProps<{
  primaryKey: string;
  isLoading: boolean;
  selection?: boolean;
  items: PaginatedList<SelectedItemsType> | null;
  displayActions?: boolean;
  modelValue: SelectedItemsType;
  resource: string;
  rowClickSelectionMode?: SelectionModeEnum;
  getDescription: (item: SelectedItemType) => string;
}>();

const emit = defineEmits([
  "pagination:getNextPage",
  "actions:detach",
  "update:modelValue",
  "rowClick",
]);

const { t } = useI18n();

const detailTable = ref();
const rowSelection: Ref<SelectedItemsType[]> = ref([]);
watch(
  () => props.items,
  async () => {
    await nextTick();
    refreshTableSelection();
  },
);

watch(
  () => props.modelValue,
  async (value) => {
    rowSelection.value = value;
    refreshTableSelection();
  },
);

/**
 * When changing selection from input
 */
function onChangeSelectInput() {
  refreshTableSelection();
  updateModel();
}

/**
 * When selecting a specific row
 * @param selection
 */
function onSelectRow(selection: SelectedItemsType[]) {
  const temporarySet = new Set<SelectedItemsType>();

  rowSelection.value
    .filter(
      (selected) =>
        !detailTable.value.data.find((row: SelectedItemsType) => {
          return row[props.primaryKey] === selected[props.primaryKey];
        }),
    )
    .forEach((value) => temporarySet.add(value));
  selection.forEach((value) => temporarySet.add(value));
  rowSelection.value = Array.from(temporarySet.values());

  updateModel();
}

function updateModel() {
  emit("update:modelValue", rowSelection.value);
}

function refreshTableSelection() {
  detailTable.value.toggleTableSelectionMode(rowSelection.value.length > 0);
  detailTable.value.data.forEach((row: Dtwin | User | GroupsUserV2) => {
    const selected = rowSelection.value.some((item: SelectedItemsType) => {
      return row[props.primaryKey] === item[props.primaryKey];
    });
    detailTable.value.toggleRowSelection(row, selected);
  });
}

async function getNextByPageAndSize(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  emit("pagination:getNextPage", page, pageSize);
}
</script>

<template>
  <div v-if="rowSelection.length !== 0" class="footer-wrapper mb-4">
    <el-select
      v-if="rowSelection.length !== 0"
      v-model="rowSelection"
      class="footer-selection"
      :value-key="primaryKey"
      multiple
      collapse-tags
      collapse-tags-tooltip
      :max-collapse-tags="3"
      reserve-keyword
      :placeholder="t('sectors.details.currentSelection')"
      :teleported="false"
      @change="onChangeSelectInput"
    >
      <el-option
        v-for="(item, index) in rowSelection"
        :key="index"
        :label="getDescription(item)"
        :value="item"
      />
    </el-select>
  </div>

  <lxc-table
    ref="detailTable"
    min-width="65rem"
    class="mb-4"
    :is-loading="isLoading"
    :data="items?.results"
    :context="items?.pagination"
    :empty-text="t(`${resource}.empty`)"
    clickable
    :row-click-selection-mode="rowClickSelectionMode"
    @select="onSelectRow"
    @select-all="onSelectRow"
    @row-click="$emit('rowClick', $event)"
    @change-page-and-page-size="getNextByPageAndSize"
  >
    <lxc-table-column v-if="selection" type="selection" />
    <slot />
    <lxc-table-column width="6rem">
      <template #default="scope">
        <sector-item-actions
          :display-actions="displayActions"
          @detach="emit('actions:detach', scope.row)"
        />
      </template>
    </lxc-table-column>
  </lxc-table>
</template>
